import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, Optional, RendererFactory2 } from '@angular/core';
import { CwcAlert } from '@cmx-web-components/angular';
import { CX_DESIGN_VERSION_TOKEN, CxUiDesignVersion } from '../cx-ui/cx-design-version/cx-design-version.config';

@Injectable({
  providedIn: 'root',
})
export class CxAlertService {
  constructor(
    private rendererFactory: RendererFactory2,
    @Inject(DOCUMENT) private document,
    @Optional() @Inject(CX_DESIGN_VERSION_TOKEN) private designVersionToken?: CxUiDesignVersion,
  ) {}

  private rdr2 = this.rendererFactory.createRenderer(null, null);
  private hostSelector = 'cmx-alerts-output .alerts';

  private createDefaultAlert(id?: string) {
    const alertElement = this.rdr2.createElement('cwc-alert');

    // Set data attribute for alert id, it helps to check if the alert is already created
    if (id) {
      alertElement.setAttribute('alert-id', id);
    }

    const $alert = alertElement as CwcAlert;

    if (this.designVersionToken) {
      $alert.designVersion = this.designVersionToken;
    }
    return $alert;
  }

  public createAlert(options: Partial<CwcAlert>, id?: string) {
    const $alert = this.createDefaultAlert(id || null);

    Object.entries(options).forEach(([parameter, value]) => {
      $alert[parameter] = value;
    });

    if ($alert.keepOpen !== undefined && !$alert.keepOpen) {
      setTimeout(
        () => {
          this.rdr2.removeChild(this.document.querySelector(this.hostSelector), $alert);
        },
        $alert.duration + 500 || 10500,
      );
    }
    this.rdr2.appendChild(this.document.querySelector(this.hostSelector), $alert);
  }

  /**
   * Removes an alert element from the DOM based on the provided alert ID.
   *
   * @param id - The unique identifier of the alert to be removed.
   */
  public removeAlert(id: string) {
    const alert = this.document.querySelector(this.hostSelector).querySelector(`cwc-alert[alert-id="${id}"]`);
    if (alert) {
      this.rdr2.removeChild(this.document.querySelector(this.hostSelector), alert);
    }
  }

  /**
   * Check if cwc alert is already created to avoid duplicates
   * @param id Id to be checked in DOM to see if the alert is already created
   * @returns Boolean `true` if the alert is already created, `false` otherwise
   */
  public isCwcAlertCreated(id: string): boolean {
    return !!this.document.querySelector(this.hostSelector).querySelector(`cwc-alert[alert-id="${id}"]`);
  }
}
